import classNames from "classnames";
import MuiSkeleton, {
  SkeletonProps as MuiSkeletonProps,
} from "@mui/material/Skeleton";

export interface SkeletonProps {
  /**
   * Height of the skeleton.
   * Can be a number (pixels) or a string (e.g., "100%").
   */
  height?: number | string;
  /**
   * Width of the skeleton.
   * Can be a number (pixels) or a string (e.g., "100%", "200px").
   */
  width?: number | string;
  /**
   * Number of skeleton elements to render.
   * When count > 1, multiple skeletons are rendered.
   */
  count?: number;
  /**
   * Class name to apply to the skeleton element.
   * This mimics the className prop from react-loading-skeleton.
   */
  className?: string;
  /**
   * Class name to apply to the container when count > 1.
   * This mimics the containerClassName prop from react-loading-skeleton.
   */
  containerClassName?: string;
  /**
   * The variant of the skeleton.
   * @default "rounded"
   */
  variant?: MuiSkeletonProps["variant"];
  /**
   * Additional props to pass to the MUI Skeleton component.
   */
  sx?: MuiSkeletonProps["sx"];
}

/**
 * Skeleton component that wraps MUI Skeleton with an API similar to react-loading-skeleton.
 * This component provides a simpler interface for common skeleton loading patterns.
 */
const Skeleton = ({
  height,
  width,
  count = 1,
  className,
  containerClassName,
  variant = "rounded",
  sx,
}: SkeletonProps) => {
  const skeletonElement = (key?: number) => (
    <MuiSkeleton
      key={key}
      data-testid="skeleton"
      variant={variant}
      height={height}
      width={width}
      className={classNames(className, variant === "rounded" && "!rounded-sm")}
      sx={{
        ...sx,
      }}
    />
  );

  if (count > 1) {
    return (
      <div className={containerClassName}>
        {Array.from({ length: count }, (_, index) => skeletonElement(index))}
      </div>
    );
  }

  return skeletonElement();
};

export default Skeleton;
